/**
 * API client for OTA firmware updates
 * Uses device-service-staging endpoints
 */

const DEVICE_SERVICE_URL = import.meta.env.VITE_DEVICE_SERVICE_URL || '';

function getAuthToken(): string | null {
  return localStorage.getItem('dashboard_zce_token');
}

function getHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface FirmwareUpdate {
  id: number;
  device_id: number;
  device_sn: string | null;
  device_name: string | null;
  firmware_url: string;
  status: string;
  update_date: string;
  created_at: string;
  updated_at: string;
}

export interface OtaDevice {
  device_id: number;
  device_sn: string;
  device_name: string | null;
  firmware_version: string | null;
  device_status: string | null;
}

export interface TriggerOtaResult {
  success: boolean;
  message?: string;
  data?: {
    device_id: string;
    firmware_update_id: number;
    firmware_url: string;
    retrieve_date: string;
  };
  error?: string;
}

export const otaApi = {
  async getHistory(limit = 20): Promise<{ success: boolean; data: FirmwareUpdate[] }> {
    const res = await fetch(`${DEVICE_SERVICE_URL}/api/v1/devices/ota/history?limit=${limit}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  },

  async getDevices(): Promise<{ success: boolean; data: OtaDevice[] }> {
    const res = await fetch(`${DEVICE_SERVICE_URL}/api/v1/devices/ota/devices`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  },

  async uploadFirmware(file: File): Promise<{ success: boolean; message?: string; data?: { filename: string; size: number; url: string }; error?: string }> {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('firmware', file);
    const res = await fetch(`${DEVICE_SERVICE_URL}/api/v1/devices/ota/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    return res.json();
  },

  async triggerOta(deviceSn: string, firmwareUrl: string): Promise<TriggerOtaResult> {
    const res = await fetch(`${DEVICE_SERVICE_URL}/api/v1/devices/${deviceSn}/ota/trigger`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ firmware_url: firmwareUrl }),
    });
    return res.json();
  },
};
